import { useMemo, useRef } from "react";

import ContentTeaser from "@/components/ContentTeaser";
import ContentVeil from "@/components/ContentVeil";
import { LANG, LANG_EN } from "@/constants/Language";
import useLangstonMediaGet from "@/content/useLangstonMediaGet";
import useHasFeature from "@/features/useHasFeature";
import uiTypes from "@/view/constants/uiTypes";
import ContentDisplayCopyright from "@/view/ContentDisplay/ContentDisplayCopyright";
import { Metadata } from "@/view/ContentHead";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import { TiptapArticleBody } from "@/view/TiptapArticleBody/TiptapArticleBody";
import getLanguageCode from "@/view/utils/getLanguageCode";
import useAccessibilityControlsContext from "@/view/utils/useAccessibilityControlsContext";
import useAnnotationsState from "@/view/utils/useAnnotationsState";
import { IContent } from "@/view/utils/useSetInitialLevel";
import useUserContentUIContext from "@/view/utils/useUserContentUIContext";
import { bodyCss, rootCss, tiptapCss } from "./ArticleBody.style";
import DecodableContentProviderLogo from "./DecodableContentProviderLogo";

interface ArticleBodyProps {
  content: IContent;
  contentMetadata: Metadata[];
  currentFontSize?: string;
  isPreview?: boolean;
  isVocabularySupportEnabled?: boolean;
  keyTermDataContentId?: string;
  language?: LANG | undefined;
  legacyArticleId?: number;
  levelId: string;
  lexile?: string;
  rawText: any; // TODO: Define proper type
  toggleVocabularySupport?: (value: boolean) => void;
}

/**
 * The ArticleBody component displays the main text elements for an article.
 */
const ArticleBody = ({
  content,
  contentMetadata,
  isPreview = false,
  isVocabularySupportEnabled,
  keyTermDataContentId,
  language = LANG_EN,
  legacyArticleId,
  levelId,
  lexile,
  rawText,
  toggleVocabularySupport,
}: ArticleBodyProps) => {
  const articleBodyRef = useRef<HTMLDivElement>(null);
  const copyrightText = content?.data?.content_provider?.copyright || "";
  const contentId = content?.id || "";

  const {
    contentDataWithAnnotations,
    annotationsIncludingPossibleDraftAnnotation,
  } = useAnnotationsState();
  const { showParagraphCount, currentFontSize } =
    useAccessibilityControlsContext();
  const media = useLangstonMediaGet(contentId);
  const [hasLiteUsersToSeeMoreTextFeature] = useHasFeature(
    "lite_users_to_see_more_text",
  );
  const { uiType } = useUserContentUIContext();
  const { isReadAloudModeActive } = useReadAloudMode();

  // This component is memoized to prevent re-renders that cause issues with focus and text selection.
  const memoized = useMemo(() => {
    if (!rawText) return;
    const liteUserPremiumContent = uiType === uiTypes.LITE_USER_PREMIUM_CONTENT;

    const renderBody = () => {
      return (
        <div
          css={tiptapCss(currentFontSize, showParagraphCount && !isPreview)}
          data-testid="tiptap-wrapper"
        >
          <TiptapArticleBody
            currentFontSize={currentFontSize}
            isPreview={isPreview}
            keyTermDataContentId={keyTermDataContentId}
            media={media}
            levelId={levelId}
            rawText={rawText}
            toggleVocabularySupport={toggleVocabularySupport}
            legacyArticleId={legacyArticleId}
            content={content}
            language={language}
            lexile={lexile}
          />
          <DecodableContentProviderLogo
            content={content}
            contentMetadata={contentMetadata}
          />
        </div>
      );
    };

    const renderLiteBody = () => {
      return hasLiteUsersToSeeMoreTextFeature ? (
        <ContentTeaser isActive>{renderBody()}</ContentTeaser>
      ) : (
        <ContentVeil />
      );
    };

    return (
      <div css={rootCss} ref={articleBodyRef}>
        <div
          css={bodyCss(
            showParagraphCount && !isReadAloudModeActive && !isPreview,
          )}
          lang={getLanguageCode(language)}
        >
          {liteUserPremiumContent ? renderLiteBody() : renderBody()}
        </div>
        <ContentDisplayCopyright copyright={copyrightText} />
      </div>
    );
  }, [
    showParagraphCount,
    isReadAloudModeActive,
    legacyArticleId,
    isVocabularySupportEnabled,
    JSON.stringify(annotationsIncludingPossibleDraftAnnotation),
    JSON.stringify(media),
    JSON.stringify(contentDataWithAnnotations),
    JSON.stringify(rawText),
    currentFontSize,
  ]);

  return <>{memoized}</>;
};

export default ArticleBody;
